import type { FC } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import ReactModal from "react-modal";
import { ButtonUI } from "../../../../../components/Button";
import { FormGroupUI } from "../../../../../components/FormGroup";
import { FormGroupLabelUI } from "../../../../../components/FormGroupLabel";
import { useCreateOrganizationRestApi } from "../../../../../hooks/useOrganizationsRestApi";

interface CreateOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  name: string;
  description: string;
  isActive: boolean;
}

export const CreateOrganizationModal: FC<CreateOrganizationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { createOrganization, loading } = useCreateOrganizationRestApi();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: "",
      description: "",
      isActive: true,
    },
  });

  const onSubmit: SubmitHandler<FormData> = async data => {
    try {
      await createOrganization({
        name: data.name.trim(),
        description: data.description.trim() || undefined,
        isActive: data.isActive,
      });

      onSuccess();
      reset();
    } catch (_error) {
      // エラーを無視
    }
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      onClose();
    }
  };

  const modalStyle = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "90%",
      maxWidth: "500px",
      maxHeight: "80vh",
      overflow: "auto",
      borderRadius: "8px",
      border: "none",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1000,
    },
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={modalStyle}
      contentLabel="新規組織作成"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">新規組織作成</h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormGroupUI>
            <FormGroupLabelUI>組織名 *</FormGroupLabelUI>
            <input
              {...register("name", {
                required: "組織名は必須です",
                minLength: {
                  value: 2,
                  message: "組織名は2文字以上で入力してください",
                },
                maxLength: {
                  value: 100,
                  message: "組織名は100文字以下で入力してください",
                },
              })}
              type="text"
              placeholder="組織名を入力"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? "border-red-300" : "border-gray-300"
              }`}
              disabled={loading}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </FormGroupUI>

          <FormGroupUI>
            <FormGroupLabelUI>説明</FormGroupLabelUI>
            <textarea
              {...register("description", {
                maxLength: {
                  value: 500,
                  message: "説明は500文字以下で入力してください",
                },
              })}
              placeholder="組織の説明を入力（任意）"
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.description ? "border-red-300" : "border-gray-300"
              }`}
              disabled={loading}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">
                {errors.description.message}
              </p>
            )}
          </FormGroupUI>

          <FormGroupUI>
            <div className="flex items-center">
              <input
                {...register("isActive")}
                type="checkbox"
                id="isActive"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                disabled={loading}
              />
              <label
                htmlFor="isActive"
                className="ml-2 block text-sm text-gray-900"
              >
                アクティブ状態で作成
              </label>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              チェックを外すと非アクティブ状態で組織が作成されます
            </p>
          </FormGroupUI>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <ButtonUI
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              キャンセル
            </ButtonUI>
            <ButtonUI
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? "作成中..." : "作成"}
            </ButtonUI>
          </div>
        </form>
      </div>
    </ReactModal>
  );
};
